import { useState } from "react";
import { useSeason } from "../../context/SeasonContext";
import { useAuth } from "../../context/AuthContext";
import {
  saveSeason,
  copySeasonContent,
  SEASON_STATUS,
  getSeasonParticipants,
  saveSeasonParticipant,
  PARTICIPATION_STATUS,
} from "../../api/seasons";
import { getAdminUsers } from "../../api/admin";
import { useAdminData, useAdminQuery, useAdminTask } from "./useAdminData";
import { fullName } from "./adminModel";
import {
  Button,
  SectionHeader,
  Notice,
  Dialog,
  Field,
  CheckField,
  AsyncPicker,
  Toolbar,
  SearchField,
  FilterSelect,
  DataTable,
  Pagination,
  ResourceContent,
  Badge,
} from "./components/AdminUI";
import styles from "./admin.module.css";
import seasonStyles from "../../components/Seasons/seasons.module.css";
const EMPTY = {
  title: "",
  slug: "",
  description: "",
  status: "draft",
  starts_on: "",
  ends_on: "",
  applications_close_on: "",
  choices_close_on: "",
  application_url: "",
  expert_application_url: "",
  is_featured: false,
};
function SeasonEditor({ item, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(
      Object.keys(EMPTY).map((key) => [key, item?.[key] ?? EMPTY[key]]),
    ),
  );
  const task = useAdminTask();
  const change = (key) => (event) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const submit = (event) => {
    event.preventDefault();
    const payload = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [
        key,
        value === "" ? null : value,
      ]),
    );
    task.run(() => saveSeason(item?.id, payload), onSaved);
  };
  return (
    <Dialog
      title={item ? "Настройки сезона" : "Новый сезон"}
      description="Подготовьте программу и кейсы, затем откройте набор. Настройки действуют только для этого сезона."
      onClose={onClose}
      busy={task.busy}
      dirty={
        JSON.stringify(form) !==
        JSON.stringify(
          Object.fromEntries(
            Object.keys(EMPTY).map((key) => [key, item?.[key] ?? EMPTY[key]]),
          ),
        )
      }
      footer={
        <>
          <Button onClick={onClose} disabled={task.busy}>
            Отмена
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="season-editor"
            disabled={task.busy}
          >
            {task.busy ? "Сохраняем…" : "Сохранить сезон"}
          </Button>
        </>
      }
    >
      <form id="season-editor" onSubmit={submit} className={styles.formGrid}>
        <Field
          label="Название"
          required
          maxLength={100}
          value={form.title}
          onChange={change("title")}
          placeholder="Весна 2027"
        />
        <Field
          label="Адрес сезона"
          required
          pattern="[a-z0-9]+(-[a-z0-9]+)*"
          minLength={3}
          maxLength={64}
          disabled={item && item.status !== "draft"}
          hint="Латинские буквы, цифры и дефисы. После публикации адрес закрепляется."
          value={form.slug}
          onChange={change("slug")}
          placeholder="spring-2027"
        />
        <Field
          label="Описание"
          type="textarea"
          wide
          maxLength={3000}
          value={form.description}
          onChange={change("description")}
        />
        <Field
          label="Этап сезона"
          value={form.status}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              status: event.target.value,
              is_featured:
                event.target.value === "draft" ? false : previous.is_featured,
            }))
          }
        >
          {Object.entries(SEASON_STATUS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Field>
        <CheckField
          label="Показывать по умолчанию"
          hint="Сезон откроется новым посетителям сайта. Предыдущий останется в архиве."
          checked={form.is_featured}
          disabled={form.status === "draft"}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              is_featured: event.target.checked,
            }))
          }
        />
        {[
          ["starts_on", "Начало"],
          ["ends_on", "Завершение"],
          ["applications_close_on", "Заявки до"],
          ["choices_close_on", "Выбор кейсов до"],
        ].map(([key, label]) => (
          <Field
            type="date"
            key={key}
            label={label}
            value={form[key]}
            onChange={change(key)}
            hint={key === "choices_close_on" ? "До конца указанного дня по Москве. После этой даты выбор и сброс приоритетов блокируются. Оставьте пустым, чтобы не ограничивать выбор по дате." : undefined}
          />
        ))}
        <Field
          label="Внешняя форма заявки"
          type="url"
          wide
          hint="Необязательно. Если оставить пустым, участники подают заявку прямо на платформе."
          value={form.application_url}
          onChange={change("application_url")}
        />
        <Field
          label="Форма для экспертов"
          type="url"
          wide
          value={form.expert_application_url}
          onChange={change("expert_application_url")}
        />
        {form.status === "completed" && (
          <Notice>
            Участники смогут только просматривать материалы. Правки
            администратором потребуют отдельного режима архива.
          </Notice>
        )}
        <Notice danger>{task.error}</Notice>
      </form>
    </Dialog>
  );
}
function CopyDialog({ target, seasons, onClose, onSaved }) {
  const [source, setSource] = useState(
    String(seasons.find((item) => item.id !== target.id)?.id || ""),
  );
  const [cases, setCases] = useState(true);
  const [schedule, setSchedule] = useState(false);
  const task = useAdminTask();
  return (
    <Dialog
      title={`Материалы для «${target.title}»`}
      description="Участники, команды, заявки и результаты не переносятся. Кейсы появятся черновиками, программа — без дат и записей."
      onClose={onClose}
      busy={task.busy}
      footer={
        <>
          <Button onClick={onClose} disabled={task.busy}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={task.busy || !source || (!cases && !schedule)}
            onClick={() =>
              task.run(
                () =>
                  copySeasonContent(target.id, {
                    source_season_id: Number(source),
                    cases,
                    schedule,
                  }),
                onSaved,
              )
            }
          >
            {task.busy ? "Копируем…" : "Скопировать материалы"}
          </Button>
        </>
      }
    >
      <Field
        label="Из сезона"
        value={source}
        onChange={(event) => setSource(event.target.value)}
      >
        {seasons
          .filter((item) => item.id !== target.id)
          .map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
      </Field>
      <CheckField
        label="Кейсы"
        checked={cases}
        onChange={(event) => setCases(event.target.checked)}
      />
      <CheckField
        label="Программа"
        checked={schedule}
        onChange={(event) => setSchedule(event.target.checked)}
      />
      <Notice>
        Копирование доступно только в пустой раздел нового сезона. Сначала
        проверьте выбранный источник.
      </Notice>
      <Notice danger>{task.error}</Notice>
    </Dialog>
  );
}
export function SeasonsSection({ notify }) {
  const { seasons, season, refresh, selectSeason } = useSeason();
  const { user } = useAuth();
  const [editor, setEditor] = useState(undefined);
  const [copy, setCopy] = useState(null);
  const admin = user?.platform_role === "ADMIN" || user?.role === "ADMIN";
  return (
    <>
      <SectionHeader
        title="Сезоны ЮФУ.Про"
        description="Подготовьте новый интенсив, откройте набор и сохраните историю прошлых сезонов."
      >
        {admin && (
          <Button variant="primary" icon="plus" onClick={() => setEditor(null)}>
            Новый сезон
          </Button>
        )}
      </SectionHeader>
      <div className={seasonStyles.grid}>
        {seasons.map((item) => (
          <article key={item.id} className={seasonStyles.card}>
            <span
              className={`${seasonStyles.status} ${seasonStyles[item.status]}`}
            >
              {SEASON_STATUS[item.status]}
              {item.is_featured ? " · На главной" : ""}
            </span>
            <h2>{item.title}</h2>
            <p>{item.description || "Описание пока не добавлено."}</p>
            <small>
              {item.id === season.id
                ? "Выбран сейчас"
                : "Отдельные участники, команды и материалы"}
            </small>
            <div className={seasonStyles.actions}>
              <Button
                onClick={() => selectSeason(item.id)}
                disabled={item.id === season.id}
              >
                Открыть
              </Button>
              {admin && (
                <Button icon="edit" onClick={() => setEditor(item)}>
                  Настроить
                </Button>
              )}
              {admin && item.status === "draft" && (
                <Button onClick={() => setCopy(item)}>
                  Перенести материалы
                </Button>
              )}
            </div>
          </article>
        ))}
      </div>
      {editor !== undefined && (
        <SeasonEditor
          item={editor}
          onClose={() => setEditor(undefined)}
          onSaved={() => {
            setEditor(undefined);
            refresh();
            notify("Сезон сохранён.");
          }}
        />
      )}
      {copy && (
        <CopyDialog
          target={copy}
          seasons={seasons}
          onClose={() => setCopy(null)}
          onSaved={(result) => {
            setCopy(null);
            refresh();
            notify(
              `Материалы скопированы: кейсов — ${result.cases || 0}, событий — ${result.schedule || 0}.`,
            );
          }}
        />
      )}
    </>
  );
}
const ROLES = {
  MEMBER: "Участник",
  MENTOR: "Наставник",
  TRACK_COORDINATOR: "Координатор сезона",
  EDE: "Эксперт",
};
function ParticipantEditor({ item, season, onClose, onSaved, admin }) {
  const [person, setPerson] = useState(item || null);
  const [form, setForm] = useState({
    status: item?.status || "accepted",
    selection_status: ["PARTICIPANT", "PARTICIPANT_PRO"].includes(
      item?.selection_status,
    )
      ? item.selection_status
      : "PARTICIPANT",
    role: item?.role || "MEMBER",
    track: item?.track || "",
  });
  const task = useAdminTask();
  const change = (key) => (event) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
  return (
    <Dialog
      title={item ? "Участие в сезоне" : "Добавить участника"}
      description={season.title}
      onClose={onClose}
      busy={task.busy}
      dirty
      footer={
        <>
          <Button onClick={onClose} disabled={task.busy}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={!person || task.busy}
            onClick={() =>
              task.run(
                () =>
                  saveSeasonParticipant(season.id, person.user_id, {
                    ...form,
                    track: form.track || null,
                    selection_status:
                      form.status === "accepted" ? form.selection_status : null,
                  }),
                onSaved,
              )
            }
          >
            {task.busy ? "Сохраняем…" : "Сохранить"}
          </Button>
        </>
      }
    >
      {item ? (
        <p>
          <strong>{fullName(item)}</strong>
          <br />
          {item.email}
        </p>
      ) : (
        <AsyncPicker
          label="Аккаунт на платформе"
          loader={getAdminUsers}
          value={person}
          onChange={setPerson}
          getLabel={fullName}
          getMeta={(value) => value.email}
          idKey="user_id"
          required
        />
      )}
      <div className={styles.formGrid}>
        <Field
          label="Статус участия"
          value={form.status}
          onChange={change("status")}
        >
          {Object.entries(PARTICIPATION_STATUS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Field>
        <Field
          label="Роль в этом сезоне"
          value={form.role}
          onChange={change("role")}
        >
          {Object.entries(ROLES)
            .filter(
              ([key]) => admin || !["TRACK_COORDINATOR", "EDE"].includes(key),
            )
            .map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
        </Field>
        {form.status === "accepted" && (
          <Field
            label="Уровень отбора"
            value={form.selection_status}
            onChange={change("selection_status")}
          >
            <option value="PARTICIPANT">Участник</option>
            <option value="PARTICIPANT_PRO">Участник Про</option>
          </Field>
        )}
        <Field
          label="Направление"
          value={form.track}
          onChange={change("track")}
          maxLength={120}
          hint="Например, ЮФУ.Про Партнеров"
        />
      </div>
      <Notice danger>{task.error}</Notice>
    </Dialog>
  );
}
export function SeasonParticipantsSection({ notify }) {
  const { season, canEdit, refresh } = useSeason();
  const { user } = useAuth();
  const query = useAdminQuery({ status: "" });
  const resource = useAdminData(getSeasonParticipants, {
    seasonId: season?.id,
    query: query.values.q,
    status: query.values.status,
    limit: query.values.limit,
    offset: query.values.offset,
  });
  const [editor, setEditor] = useState(undefined);
  return (
    <>
      <SectionHeader
        title="Участники сезона"
        description={`${season?.title} · Заявки, отбор и роли. Изменения не затрагивают другие сезоны.`}
      >
        <Button
          variant="primary"
          icon="plus"
          disabled={!canEdit}
          onClick={() => setEditor(null)}
        >
          Добавить участника
        </Button>
      </SectionHeader>
      <Toolbar
        total={resource.total}
        onRefresh={resource.refresh}
        loading={resource.loading}
      >
        <SearchField
          value={query.values.q}
          onChange={(q) => query.update({ q })}
          placeholder="Имя или email"
        />
        <FilterSelect
          label="Статус"
          value={query.values.status}
          onChange={(status) => query.update({ status })}
          options={[
            { value: "", label: "Все статусы" },
            ...Object.entries(PARTICIPATION_STATUS).map(([value, label]) => ({
              value,
              label,
            })),
          ]}
        />
      </Toolbar>
      <ResourceContent
        resource={resource}
        emptyTitle="Участников пока нет"
        emptyDescription="Откройте набор, добавьте участников вручную или импортируйте результаты отбора."
      >
        <DataTable
          items={resource.items}
          rowKey="user_id"
          caption="Участники выбранного сезона"
          columns={[
            {
              key: "person",
              label: "Участник",
              render: (item) => (
                <div>
                  <strong className={styles.cellTitle}>{fullName(item)}</strong>
                  <div className={styles.cellMeta}>{item.email}</div>
                </div>
              ),
            },
            {
              key: "status",
              label: "Статус",
              render: (item) => (
                <Badge
                  tone={item.status === "accepted" ? "success" : "neutral"}
                >
                  {PARTICIPATION_STATUS[item.status]}
                </Badge>
              ),
            },
            {
              key: "role",
              label: "Роль",
              render: (item) => (
                <div>
                  <span>{ROLES[item.role]}</span>
                  <div className={styles.cellMeta}>
                    {item.track || "Без направления"}
                  </div>
                </div>
              ),
            },
            {
              key: "actions",
              label: "",
              render: (item) => (
                <Button disabled={!canEdit} onClick={() => setEditor(item)}>
                  Изменить
                </Button>
              ),
            },
          ]}
        />
      </ResourceContent>
      <Pagination query={query} resource={resource} />
      {editor !== undefined && (
        <ParticipantEditor
          item={editor}
          season={season}
          admin={user?.role === "ADMIN"}
          onClose={() => setEditor(undefined)}
          onSaved={() => {
            setEditor(undefined);
            resource.refresh();
            refresh();
            notify("Участие в сезоне обновлено.");
          }}
        />
      )}
    </>
  );
}
